import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Bot, Upload, FileText, Image as ImageIcon } from "lucide-react";
import AiImportProgress from "./ai-import/AiImportProgress";
import AiImportReview from "./ai-import/AiImportReview";
import { loadActiveCurrencies, getUserDefaultCurrency } from "@/services/ai-import/aiImportService";
import { logger } from "@/services/LoggingService";
import { useAiImportPortfolios } from "@/components/assets/ai-import/useAiImportPortfolios";
import { useAiImportWizard } from "@/components/assets/ai-import/useAiImportWizard";

interface AiImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImported?: () => void;
  initialFiles?: File[];
}

export function AiImportDialog({ open, onOpenChange, onImported, initialFiles }: AiImportDialogProps) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  // Initialize wizard first to provide `step` to portfolios hook
  const {
    step,
    progressPct,
    statusMessage,
    items,
    setItems,
    isCommitting,
    onFilesSelected,
    handleAbort,
    handleRescan,
    handleEdit,
    handleCommit,
    setStep,
    jobId,
    fileNames,
  } = useAiImportWizard({ selectedPortfolioId: null, onImported, onClose: () => onOpenChange(false) });
  // Now wire portfolios with the computed step
  const { portfolios, selectedPortfolioId, setSelectedPortfolioId, isCreatingPortfolio, handleCreatePortfolio } = useAiImportPortfolios(step === 'review');
  // Multi-currency support
  const [currencies, setCurrencies] = useState<Array<{ code: string; name?: string; symbol?: string }>>([]);
  const [defaultCurrency, setDefaultCurrency] = useState<string | null>(null);
  // Ensure we only prefill once per review session
  const prefillAppliedRef = useRef(false);
  // Background appends are managed inside useAiImportWizard

  const handleUploadClick = () => fileInputRef.current?.click();
  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []).filter(Boolean);
    if (files.length === 0) return;
    onFilesSelected(files);
  };

  const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(true);
  };
  const onDragLeave = () => setIsDragOver(false);
  const onDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = Array.from(e.dataTransfer.files || []).filter(Boolean);
    if (files.length === 0) return;
    await onFilesSelected(files);
  };

  // Polling moved into useAiImportWizard

  // Auto-start when opened with initial files on intro step
  const bootOnceRef = useRef(false);
  useEffect(() => {
    if (!open) {
      bootOnceRef.current = false;
      return;
    }
    if (bootOnceRef.current) return;
    if (step === 'intro' && Array.isArray(initialFiles) && initialFiles.length > 0) {
      bootOnceRef.current = true;
      onFilesSelected(initialFiles);
    }
  }, [open, step, initialFiles, onFilesSelected]);

  // Block accidental close/refresh during processing/review/commit
  useEffect(() => {
    const beforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    if (step === 'processing' || step === 'review' || isCommitting) {
      window.addEventListener('beforeunload', beforeUnload);
      return () => window.removeEventListener('beforeunload', beforeUnload);
    }
    return;
  }, [step, isCommitting]);

  // Load currencies and user's default currency when entering review
  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const cur = await loadActiveCurrencies();
        if (!cancelled) setCurrencies(cur as any);
      } catch {}

      try {
        const dc = await getUserDefaultCurrency();
        if (!cancelled) setDefaultCurrency(dc || 'CAD');
      } catch {
        if (!cancelled) setDefaultCurrency('CAD');
      }
    }
    if (step === 'review') load();
    return () => { cancelled = true; };
  }, [step]);

  // Prefill missing row currencies from user's default currency (one-time during review)
  useEffect(() => {
    if (step !== 'review') {
      prefillAppliedRef.current = false;
      return;
    }
    if (!defaultCurrency || prefillAppliedRef.current) return;
    setItems(prev => (prev || []).map(it => {
      const c = (it.currency || '').trim();
      return c ? { ...it, currency: c.toUpperCase() } : { ...it, currency: defaultCurrency };
    }));
    prefillAppliedRef.current = true;
  }, [step, defaultCurrency]);

  // Portfolios are handled by useAiImportPortfolios

  // Abort handled by useAiImportWizard

  // Rescan handled by useAiImportWizard

  // Row edits handled by useAiImportWizard

  // Commit handled by useAiImportWizard

  useEffect(() => {
    if (open) return;

    const cleanup = () => {
      try {
        const wrapper = document.querySelector('.group\\/sidebar-wrapper') as HTMLElement | null;
        if (wrapper?.hasAttribute('inert')) wrapper.removeAttribute('inert');
        if (wrapper?.getAttribute('aria-hidden') === 'true') wrapper.removeAttribute('aria-hidden');

        const hasOpenRadix = !!document.querySelector('[data-radix-portal] [data-state="open"]');

        let didUnlock = false;
        let bodyComputedPointerEvents: string | null = null;
        let rootComputedPointerEvents: string | null = null;
        let htmlComputedPointerEvents: string | null = null;

        if (!hasOpenRadix) {
          const body = document.body;
          bodyComputedPointerEvents = body ? getComputedStyle(body).pointerEvents : null;
          const bodyInlinePointerEvents = body?.style?.pointerEvents || '';
          const bodyHasInlineNone = bodyInlinePointerEvents === 'none' || (body?.getAttribute('style') || '').includes('pointer-events: none');
          if (bodyHasInlineNone || bodyComputedPointerEvents === 'none') {
            body.style.pointerEvents = 'auto';
            didUnlock = true;
          }
          if (body?.style?.overflow === 'hidden') {
            body.style.overflow = '';
          }

          const root = document.getElementById('root');
          rootComputedPointerEvents = root ? getComputedStyle(root).pointerEvents : null;
          const rootInlinePointerEvents = root?.style?.pointerEvents || '';
          const rootHasInlineNone = rootInlinePointerEvents === 'none' || (root?.getAttribute('style') || '').includes('pointer-events: none');
          if (root && (rootHasInlineNone || rootComputedPointerEvents === 'none')) {
            root.style.pointerEvents = 'auto';
            didUnlock = true;
          }

          const html = document.documentElement;
          htmlComputedPointerEvents = html ? getComputedStyle(html).pointerEvents : null;
          const htmlInlinePointerEvents = html?.style?.pointerEvents || '';
          const htmlHasInlineNone = htmlInlinePointerEvents === 'none' || (html?.getAttribute('style') || '').includes('pointer-events: none');
          if (htmlHasInlineNone || htmlComputedPointerEvents === 'none') {
            html.style.pointerEvents = 'auto';
            didUnlock = true;
          }
          if (html?.style?.overflow === 'hidden') {
            html.style.overflow = '';
          }
        }

        if (didUnlock) {
          const inertCount = document.querySelectorAll('[inert]').length;
          logger.debug('AiImportDialog', 'Dialog closed cleanup completed', {
            inertCount,
            hasOpenRadix,
            bodyPointerEvents: document.body?.style?.pointerEvents || null,
            bodyComputedPointerEvents,
            bodyOverflow: document.body?.style?.overflow || null,
            rootPointerEvents: document.getElementById('root')?.style?.pointerEvents || null,
            rootComputedPointerEvents,
            htmlPointerEvents: document.documentElement?.style?.pointerEvents || null,
            htmlComputedPointerEvents,
          });
        }
      } catch (error) {
        logger.warn('AiImportDialog', 'Dialog closed cleanup failed', { error: String(error) });
      }
    };

    const ids = [0, 50, 150, 400].map((ms) => window.setTimeout(cleanup, ms));
    return () => ids.forEach((id) => window.clearTimeout(id));
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(o) => {
      onOpenChange(o);
      if (!o) {
        // reset when closing
        setTimeout(() => { setStep("intro"); setItems([]); }, 150);
      }
    }}>
      <DialogContent
        onEscapeKeyDown={(e) => { if (step === 'processing' || step === 'review' || isCommitting) e.preventDefault(); }}
        onPointerDownOutside={(e) => { if (step === 'processing' || step === 'review' || isCommitting) e.preventDefault(); }}
        onInteractOutside={(e) => { if (step === 'processing' || step === 'review' || isCommitting) e.preventDefault(); }}
        className={
        "p-0 max-h-[85vh] overflow-hidden " +
        (step === "review" ? "sm:max-w-[1100px]" : step === "intro" ? "sm:max-w-[760px]" : "sm:max-w-[960px]")
      }>
        <div className={"grid h-full " + (step === "review" ? "md:grid-cols-1" : "md:grid-cols-[1.35fr_1fr]")}>
          {/* Left */}
          <div className="p-6 md:p-7 overflow-y-auto h-full">
            <DialogHeader className="p-0">
              <div className="flex items-center gap-2 mb-1">
                <Badge variant="secondary" className="uppercase tracking-wide text-[10px]">AI</Badge>
                <span className="text-xs text-muted-foreground">Import</span>
              </div>
              <DialogTitle className="text-2xl font-semibold">Ai-powered Import</DialogTitle>
              <DialogDescription />
            </DialogHeader>

            {step === "intro" && (
            <div className="mt-4 space-y-3 text-sm leading-relaxed">
              <p className="font-medium">Can't connect your online accounts? (and don't want to add them all manually?)</p>
              <p>Ai-powered Import can help!</p>
              <p>Upload a PDF, CSV, or screenshots that lists your investments showing their latest values.</p>
              <p>After scanning the files, the relevant data is extracted, converted into our format, and a preview is generated for your review.</p>
              <p>Review the details, make any necessary adjustments, and add them to your portfolio as manual entries in moments.</p>
              <div className="rounded-md border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
                <span className="font-medium">Note:</span> Ai-Import does not modify connected accounts — it only updates manually tracked entries.
              </div>

              {/* Accepted types */}
              <div className="flex items-center gap-2 pt-1 text-xs text-muted-foreground">
                <FileText className="h-4 w-4" /> PDF • CSV
                <span className="mx-1">|</span>
                <ImageIcon className="h-4 w-4" /> PNG/JPG
              </div>

              {/* Upload action / dropzone */}
              <div
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                onDrop={onDrop}
                className={
                  `mt-3 rounded-md border-dashed border p-3 transition-colors ` +
                  (isDragOver ? 'border-primary bg-primary/5' : 'border-muted-foreground/20 hover:border-muted-foreground/40')
                }
                role="button"
                tabIndex={0}
                onClick={handleUploadClick}
                aria-label="Upload files"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="text-sm">
                    <div className="font-medium">Upload Files</div>
                    <div className="text-xs text-muted-foreground">or drag & drop here</div>
                  </div>
                  <Button size="sm" className="gap-2">
                    <Upload className="h-4 w-4" />
                    Choose File
                  </Button>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="application/pdf,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,image/*"
                  className="hidden"
                  multiple
                  onChange={handleFileSelected}
                />
              </div>
            </div>
            )}

            {step === "processing" && (
              <AiImportProgress jobId={jobId || undefined} progressPct={progressPct} message={statusMessage} onAbort={handleAbort} filenames={fileNames()} />
            )}

            {step === "review" && (
            <AiImportReview
              items={items}
              onEdit={handleEdit}
              onRescan={handleRescan}
              onCommit={() => handleCommit(selectedPortfolioId)}
              isCommitting={isCommitting}
              currencies={currencies}
            />
          )}
          </div>

          {/* Right accent panel (hidden on review to use full width) */}
          {step !== "review" && (
            <div className="hidden md:flex items-center justify-center bg-muted/50 border-l">
              <div className="relative">
                <div className="absolute inset-0 -translate-x-6 -translate-y-6 blur-2xl rounded-full bg-primary/10" />
                <div className="relative rounded-xl border bg-background p-6 text-muted-foreground shadow-sm flex items-center justify-center">
                  {step === "intro" ? (
                    <IdleBotSvg className="h-16 w-16" />
                  ) : step === "processing" ? (
                    <ActiveBotSvg className="h-16 w-16" />
                  ) : (
                    <Bot className="h-16 w-16" />
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default AiImportDialog;

export function IdleBotSvg({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      role="img"
      aria-label="Robot (idle)"
      xmlns="http://www.w3.org/2000/svg"
    >
      <style>
        {`.stroke{stroke:currentColor;stroke-width:1.25;stroke-linecap:round;stroke-linejoin:round;fill:none}
          @media (prefers-reduced-motion:reduce){ path[begin], line[begin] { display:none } }`}
      </style>
      <path className="stroke" d="M12 8V4H8"/>
      <rect className="stroke" x="4" y="8" width="16" height="12" rx="2"/>
      <path className="stroke" d="M2 14h2"/>
      <path className="stroke" d="M20 14h2"/>
      <line className="stroke" x1="9" y1="13" x2="9" y2="15">
        <animate attributeName="y1" values="13;13;14;13" keyTimes="0;0.955;0.99;1" dur="3.2s" repeatCount="indefinite"/>
        <animate attributeName="y2" values="15;15;14;15" keyTimes="0;0.955;0.99;1" dur="3.2s" repeatCount="indefinite"/>
      </line>
      <line className="stroke" x1="15" y1="13" x2="15" y2="15">
        <animate attributeName="y1" values="13;13;14;13" keyTimes="0;0.955;0.99;1" dur="3.2s" begin="0.05s" repeatCount="indefinite"/>
        <animate attributeName="y2" values="15;15;14;15" keyTimes="0;0.955;0.99;1" dur="3.2s" begin="0.05s" repeatCount="indefinite"/>
      </line>
    </svg>
  );
}

export function ActiveBotSvg({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      role="img"
      aria-label="Robot (active)"
      xmlns="http://www.w3.org/2000/svg"
    >
      <style>
        {`.stroke{stroke:currentColor;stroke-width:1.25;stroke-linecap:round;stroke-linejoin:round;fill:none}
          @media (prefers-reduced-motion:reduce){ path[begin], line[begin] { display:none } }`}
      </style>
      <path className="stroke" d="M12 8V4H8"/>
      <rect className="stroke" x="4" y="8" width="16" height="12" rx="2"/>
      <path className="stroke" d="M2 14h2"/>
      <path className="stroke" d="M20 14h2"/>
      <line className="stroke" x1="9" y1="13" x2="9" y2="15">
        <animate attributeName="y1" values="13;13;14;13" keyTimes="0;0.955;0.99;1" dur="3.2s" repeatCount="indefinite"/>
        <animate attributeName="y2" values="15;15;14;15" keyTimes="0;0.955;0.99;1" dur="3.2s" repeatCount="indefinite"/>
        <animate attributeName="x1" values="9;8;10;9" keyTimes="0;0.33;0.66;1" dur="2.2s" repeatCount="indefinite"/>
        <animate attributeName="x2" values="9;8;10;9" keyTimes="0;0.33;0.66;1" dur="2.2s" repeatCount="indefinite"/>
      </line>
      <line className="stroke" x1="15" y1="13" x2="15" y2="15">
        <animate attributeName="y1" values="13;13;14;13" keyTimes="0;0.955;0.99;1" dur="3.2s" begin="0.05s" repeatCount="indefinite"/>
        <animate attributeName="y2" values="15;15;14;15" keyTimes="0;0.955;0.99;1" dur="3.2s" begin="0.05s" repeatCount="indefinite"/>
        <animate attributeName="x1" values="15;14;16;15" keyTimes="0;0.33;0.66;1" dur="2.2s" repeatCount="indefinite"/>
        <animate attributeName="x2" values="15;14;16;15" keyTimes="0;0.33;0.66;1" dur="2.2s" repeatCount="indefinite"/>
      </line>
    </svg>
  );
}
